import * as yup from 'yup';
import { VALIDATION_MESSAGES } from './constants';

// Client Details Validation
export const clientDetailsSchema = yup.object({
  firstName: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  lastName: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  email: yup.string().email(VALIDATION_MESSAGES.EMAIL).required(VALIDATION_MESSAGES.REQUIRED),
  phone: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  address: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  suburb: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  state: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  postcode: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  isNewClient: yup.boolean(),
  tfn: yup.string().when('isNewClient', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  bankName: yup.string().when('isNewClient', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  bsb: yup.string().when('isNewClient', {
    is: true,
    then: (schema) => schema
      .matches(/^\d{6}$/, VALIDATION_MESSAGES.BSB)
      .required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  accountNumber: yup.string().when('isNewClient', {
    is: true,
    then: (schema) => schema
      .matches(/^\d{4,9}$/, VALIDATION_MESSAGES.ACCOUNT_NUMBER)
      .required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  accountName: yup.string().when('isNewClient', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  })
});

// Income Validation - Simple YES/NO checklist
export const incomeSchema = yup.object({
  // Item 1 - Salary or Wages
  hasSalaryWages: yup.boolean(),
  
  // Item 2 - Allowances, Tips, Directors Fees
  hasAllowancesTips: yup.boolean(),
  
  // Item 3 - Employer Lump Sum Payments
  hasEmployerLumpSum: yup.boolean(),
  
  // Item 4 - Employment Termination Payments
  hasEmploymentTermination: yup.boolean(),
  
  // Item 5 - Australian Govt Allowances & Payments
  hasAustGovtAllowances: yup.boolean(),
  
  // Item 6 - Aust Gov Pensions and Allowances
  hasAustGovtPensions: yup.boolean(),
  
  // Item 7 - Aust Annuities & Super Income Streams
  hasAustAnnuitiesSuper: yup.boolean(),
  
  // Item 8 - Australian Super Lump Sum Payments
  hasAustSuperLumpSum: yup.boolean(),
  
  // Item 9 - Attributed Personal Services Income
  hasAttributedPersonalServices: yup.boolean(),
  
  // Item 10 - Interest Received
  hasInterest: yup.boolean(),
  
  // Item 11 - Dividends
  hasDividends: yup.boolean(),
  
  // Item 12 - Employee Share Scheme
  hasEmployeeShareScheme: yup.boolean(),
  
  // Item 13 - Partnerships/Trusts
  hasPartnershipsTrusts: yup.boolean(),
  
  // Item 18 - Capital Gains Tax
  hasCapitalGains: yup.boolean(),
  
  // Item 20 - Foreign Income
  hasForeignIncome: yup.boolean(),
  
  // Item 21 - Rental Income and Expenses
  hasRental: yup.boolean(),
  
  // Item 24 - Any Other Income
  hasOtherIncome: yup.boolean()
});

// Deductions Validation
export const deductionsSchema = yup.object({
  hasVehicle: yup.boolean(),
  vehicleMethod: yup.string().when('hasVehicle', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  logbookPercentage: yup.number().when('vehicleMethod', {
    is: 'Logbook Method (must be completed every 5 years)',
    then: (schema) => schema.min(0, 'Must be 0 or greater').max(100, 'Must be 100 or less').required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  kilometres: yup.number().when('vehicleMethod', {
    is: 'Cents per Kilometre Method (85 cents per KM from 1 July 2024)',
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasClothing: yup.boolean(),
  clothingAmount: yup.number().when('hasClothing', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasEducation: yup.boolean(),
  educationAmount: yup.number().when('hasEducation', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasMobile: yup.boolean(),
  mobileAmount: yup.number().when('hasMobile', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  mobilePercentage: yup.number().when('hasMobile', {
    is: true,
    then: (schema) => schema.min(0, 'Must be 0 or greater').max(100, 'Must be 100 or less').required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasHomeOffice: yup.boolean(),
  homeOfficeHours: yup.number().when('hasHomeOffice', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  homeOfficeRate: yup.string().when('hasHomeOffice', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasTools: yup.boolean(),
  toolsAmount: yup.number().when('hasTools', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasProfessional: yup.boolean(),
  professionalAmount: yup.number().when('hasProfessional', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  hasOtherDeductions: yup.boolean(),
  otherDeductionsAmount: yup.number().when('hasOtherDeductions', {
    is: true,
    then: (schema) => schema.positive(VALIDATION_MESSAGES.POSITIVE_NUMBER).required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  otherDeductionsDescription: yup.string().when('hasOtherDeductions', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  })
});

// Tax Offsets & Health Validation
export const taxOffsetsSchema = yup.object({
  taxOffset: yup.string().required(VALIDATION_MESSAGES.REQUIRED),
  hasPrivateHealth: yup.boolean(),
  healthCoverCode: yup.string().when('hasPrivateHealth', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  dependentChildren: yup.number().min(0, 'Must be 0 or greater').nullable().default(0),
  isMarried: yup.boolean()
});

// Spouse Info Validation
export const spouseInfoSchema = yup.object({
  spouseFirstName: yup.string().when('isMarried', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  spouseLastName: yup.string().when('isMarried', {
    is: true,
    then: (schema) => schema.required(VALIDATION_MESSAGES.REQUIRED),
    otherwise: (schema) => schema.nullable()
  }),
  spouseIncome: yup.number().when('isMarried', {
    is: true,
    then: (schema) => schema.min(0, 'Must be 0 or greater').nullable(),
    otherwise: (schema) => schema.nullable()
  }),
  spouseFringeBenefits: yup.number().when('isMarried', {
    is: true,
    then: (schema) => schema.min(0, 'Must be 0 or greater').nullable(),
    otherwise: (schema) => schema.nullable()
  }),
  spouseRentalLosses: yup.number().when('isMarried', {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.nullable()
  }),
  spouseGovernmentPensions: yup.number().when('isMarried', {
    is: true,
    then: (schema) => schema.min(0, 'Must be 0 or greater').nullable(),
    otherwise: (schema) => schema.nullable()
  })
});

// Complete form validation
export const completeFormSchema = yup.object({
  clientDetails: clientDetailsSchema,
  income: incomeSchema,
  deductions: deductionsSchema,
  taxOffsets: taxOffsetsSchema,
  spouseInfo: spouseInfoSchema
}); 